import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Download, RefreshCw, Lock, Mail, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function Admin() {
  const [params] = useSearchParams();
  const [key, setKey] = useState(params.get("key") || "");
  const [submittedKey, setSubmittedKey] = useState(params.get("key") || "");
  const [entries, setEntries] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (k) => {
    if (!k) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/waitlist/list`, { params: { key: k } });
      setEntries(data.entries || []);
      setCount(data.count || 0);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Failed to load. Check the key.");
      setEntries([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (submittedKey) load(submittedKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedKey]);

  const onUnlock = (e) => {
    e.preventDefault();
    setSubmittedKey(key.trim());
  };

  const downloadCsv = () => {
    if (!submittedKey) return;
    const url = `${API}/waitlist/export?key=${encodeURIComponent(submittedKey)}`;
    window.open(url, "_blank");
  };

  // Lock screen
  if (!submittedKey || error) {
    return (
      <div className="min-h-screen bg-[var(--halo-cream)] flex items-center justify-center px-6 py-12">
        <form
          onSubmit={onUnlock}
          className="w-full max-w-md bg-white border border-[var(--halo-border)] rounded-2xl p-8 shadow-xl"
          data-testid="admin-unlock-form"
        >
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={14} className="text-[var(--halo-ink)]" strokeWidth={1.6} />
            <span className="font-display text-2xl tracking-[0.18em]">HALO</span>
          </div>
          <p className="overline mb-6 mt-2">Admin · Waitlist</p>
          <label className="overline block mb-2">Access Key</label>
          <input
            type="password"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            placeholder="Paste your export key"
            className="halo-input"
            autoFocus
            data-testid="admin-key-input"
          />
          {error && (
            <p className="mt-3 text-sm text-red-600" data-testid="admin-error">
              {error}
            </p>
          )}
          <button type="submit" className="btn-halo w-full mt-6" data-testid="admin-unlock-submit">
            <Lock size={14} strokeWidth={1.6} /> Unlock
          </button>
          <p className="mt-6 text-xs text-[var(--halo-ink)]/55">
            Tip: bookmark this page with <code className="font-mono">?key=YOUR_KEY</code> in the URL for one-click access.
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--halo-cream)] py-12 px-6 md:px-10" data-testid="admin-dashboard">
      <div className="max-w-6xl mx-auto">
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Sparkles size={14} className="text-[var(--halo-ink)]" strokeWidth={1.6} />
              <span className="font-display text-2xl tracking-[0.18em]">HALO</span>
            </div>
            <p className="overline">The Waitlist</p>
            <h1 className="font-display text-4xl md:text-5xl mt-2 leading-tight">
              <span data-testid="admin-count">{count}</span>{" "}
              <span className="italic text-[var(--halo-terracotta)]">
                {count === 1 ? "subscriber" : "subscribers"}
              </span>
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => load(submittedKey)}
              className="btn-ghost-halo"
              disabled={loading}
              data-testid="admin-refresh"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button onClick={downloadCsv} className="btn-halo" data-testid="admin-download-csv">
              <Download size={14} strokeWidth={1.8} /> Download CSV
            </button>
          </div>
        </header>

        <div className="bg-white border border-[var(--halo-border)] rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-[var(--halo-ink)]/60" data-testid="admin-loading">
              Loading…
            </div>
          ) : entries.length === 0 ? (
            <div className="p-12 text-center" data-testid="admin-empty">
              <Mail size={28} className="mx-auto text-[var(--halo-ink)]/30" strokeWidth={1.2} />
              <p className="mt-4 font-display text-2xl">No signups yet.</p>
              <p className="mt-2 text-sm text-[var(--halo-ink)]/60">
                Your waitlist will appear here as people join.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="admin-table">
                <thead>
                  <tr className="bg-[var(--halo-stone)] text-left">
                    <th className="overline px-6 py-4 whitespace-nowrap">Email</th>
                    <th className="overline px-6 py-4 whitespace-nowrap">Name</th>
                    <th className="overline px-6 py-4 whitespace-nowrap">Source</th>
                    <th className="overline px-6 py-4 whitespace-nowrap">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr
                      key={e.id || i}
                      className="border-t border-[var(--halo-border)] hover:bg-[var(--halo-stone)]/50 transition-colors"
                      data-testid={`admin-row-${i}`}
                    >
                      <td className="px-6 py-4 font-medium text-[var(--halo-ink)] whitespace-nowrap">
                        {e.email}
                      </td>
                      <td className="px-6 py-4 text-[var(--halo-ink)]/80 whitespace-nowrap">
                        {e.name || "—"}
                      </td>
                      <td className="px-6 py-4 text-[var(--halo-ink)]/60 whitespace-nowrap">
                        {e.source || "—"}
                      </td>
                      <td className="px-6 py-4 text-[var(--halo-ink)]/60 whitespace-nowrap">
                        {formatDate(e.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <p className="mt-6 text-xs text-[var(--halo-ink)]/50">
          Showing the most recent signups first. CSV export includes all fields.
        </p>
      </div>
    </div>
  );
}
